/*
 * Lemonade Training Oy copy rights all right reserved.
 * AI Coach Suggestion Type Definitions
 */

import { Timestamp } from "firebase/firestore";
import { RadarAxis } from "./movementRegistry";

/**
 * Types of triggers that generate AI coach suggestions
 */
export const SUGGESTION_TRIGGER = {
  AXIS_IMBALANCE: "axis_imbalance",
  STALLED_PROGRESS: "stalled_progress",
  APPROACHING_MILESTONE: "approaching_milestone",
  INACTIVITY: "inactivity",
  NEW_PR: "new_pr",
} as const;

export type SuggestionTrigger =
  (typeof SUGGESTION_TRIGGER)[keyof typeof SUGGESTION_TRIGGER];

/**
 * User feedback on an AI coach suggestion
 */
export const SUGGESTION_FEEDBACK = {
  THUMBS_UP: "thumbs_up",
  THUMBS_DOWN: "thumbs_down",
  DISMISSED: "dismissed",
} as const;

export type SuggestionFeedback =
  (typeof SUGGESTION_FEEDBACK)[keyof typeof SUGGESTION_FEEDBACK];

/**
 * AI Coach suggestion document
 * Stored in: Users/{uid}/AISuggestions/{suggestionID}
 *
 * Generated by server-side rule engine (Cloud Functions),
 * never by client code. Max 1 active suggestion per session.
 *
 * @example
 * ```typescript
 * const suggestion: AICoachSuggestion = {
 *   id: "suggestion_abc",
 *   trigger: "axis_imbalance",
 *   relatedAxis: "GYM",
 *   insight: "Your Olympic Power jumped 12 points this month, but Gymnastics dropped 2.",
 *   reason: "A balanced profile unlocks higher levels faster.",
 *   action: "Try adding 3x/week strict pull-ups.",
 *   ctaText: "See Suggested Plan",
 *   ctaAction: "navigate_to_axis",
 *   ctaPayload: { axis: "GYM" },
 *   feedback: null,
 *   dismissed: false,
 *   createdAt: Timestamp.now(),
 *   expiresAt: Timestamp.fromDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
 * };
 * ```
 */
export type AICoachSuggestion = {
  /** Unique suggestion identifier */
  id: string;
  /** What triggered this suggestion */
  trigger: SuggestionTrigger;
  /** Related radar axis, if applicable */
  relatedAxis: RadarAxis | null;
  /** The insight — what the coach noticed */
  insight: string;
  /** Why it matters — brief rationale */
  reason: string;
  /** Actionable step — what to do about it */
  action: string;
  /** CTA button text */
  ctaText: string;
  /** CTA action type */
  ctaAction:
    | "navigate_to_axis"
    | "navigate_to_movement"
    | "navigate_to_workout"
    | "dismiss";
  /** CTA action payload (axis name, movement ID, etc.) */
  ctaPayload: Record<string, string> | null;
  /** User feedback, null if no feedback given */
  feedback: SuggestionFeedback | null;
  /** Whether the user dismissed this suggestion */
  dismissed: boolean;
  /** When this suggestion was generated */
  createdAt: Timestamp | Date;
  /** When this suggestion expires (auto-cleanup) */
  expiresAt: Timestamp | Date;
  /** When feedback was provided or suggestion was dismissed */
  updatedAt?: Timestamp | Date;
};

/**
 * Type guard for AICoachSuggestion
 *
 * @example
 * ```typescript
 * if (isAICoachSuggestion(data)) {
 *   console.log(data.insight); // TypeScript knows data is AICoachSuggestion
 * }
 * ```
 */
export function isAICoachSuggestion(obj: unknown): obj is AICoachSuggestion {
  if (typeof obj !== "object" || obj === null) return false;
  const suggestion = obj as AICoachSuggestion;
  return (
    typeof suggestion.id === "string" &&
    typeof suggestion.trigger === "string" &&
    typeof suggestion.insight === "string" &&
    typeof suggestion.action === "string"
  );
}

/* -------------- [ API Request Types ] ------------------ */

/**
 * Request payload for AI coach feedback
 */
export type AICoachFeedbackRequest = {
  /** Suggestion ID */
  suggestionID: string;
  /** Feedback type */
  feedback: SuggestionFeedback;
};
